import {
  ExtraFieldFormInfoFragment,
  FieldType,
} from '../../__generated__/graphql';
import { getExtraFieldInputName, recordTypeFormKey } from './logic';
import { useObjectExtraFields } from './useObjectExtraFields';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import * as yup from 'yup-legacy';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { TestFunction, TestOptionsMessage } from 'yup-legacy';
import i18next from 'i18next';
import { ExtraFieldsVisibility } from './extraFieldsVisibility';
import { useCallback, useMemo } from 'react';
import {
  ExtraFieldContextParam,
  ExtraFieldsFormId,
  ExtraFieldsSupportedObject,
} from './types';

type Props = {
  objectApiName: ExtraFieldsSupportedObject;
  formId: ExtraFieldsFormId;
  contextParams: ExtraFieldContextParam[];
};

export const useExtraFieldsFormValidationByFormId = ({
  objectApiName,
  contextParams,
  formId,
}: Props) => {
  const { getFormFields, loading } = useObjectExtraFields(objectApiName);

  const extraFieldFormInfos = getFormFields(formId);
  const { extraFieldsValidationSchema, applyExtraFieldsValidationSchema } =
    useExtraFieldsFormValidation({
      extraFieldFormInfos,
      contextParams,
    });
  return {
    extraFieldsValidationSchema,
    applyExtraFieldsValidationSchema,
    loading,
  };
};

export const useExtraFieldsFormValidation = ({
  extraFieldFormInfos,
  contextParams,
}: {
  extraFieldFormInfos: readonly ExtraFieldFormInfoFragment[];
  contextParams: ExtraFieldContextParam[];
}) => {
  const extraFieldsValidationSchema = useMemo(() => {
    const requiredExtraFields = extraFieldFormInfos.filter(
      (f) => f.required && f.fieldInfo.type !== FieldType.Boolean,
    );
    if (!requiredExtraFields.length) {
      return null;
    }
    const visibility: ExtraFieldsVisibility = new ExtraFieldsVisibility();
    return requiredExtraFields?.map((f) => [
      getExtraFieldInputName(f.fieldApiName, f.fieldInfo.type),
      getYupValidation(visibility, f, contextParams),
    ]);
  }, [extraFieldFormInfos, contextParams]);

  const applyExtraFieldsValidationSchema = useCallback(
    (schema: yup.ObjectSchema<any>) => {
      if (
        !extraFieldsValidationSchema ||
        Object.keys(extraFieldsValidationSchema).length === 0
      ) {
        return schema;
      }
      return schema.shape(Object.fromEntries(extraFieldsValidationSchema));
    },
    [extraFieldsValidationSchema],
  );

  return {
    extraFieldsValidationSchema,
    applyExtraFieldsValidationSchema,
  };
};

function getYupValidation(
  visibility: ExtraFieldsVisibility,
  field: ExtraFieldFormInfoFragment,
  contextParams: ExtraFieldContextParam[],
) {
  function requiredIfVisible(this: yup.TestContext, value: any) {
    const formatData = this.parent;
    const isVisible = visibility.isFieldVisible(field, {
      recordTypeName: formatData[recordTypeFormKey],
      contextParams,
    });
    if (!isVisible) {
      return true;
    }
    return value !== null && value !== undefined && value !== '';
  }

  function addRequiredIfVisible<T>(schema: {
    test(name: string, message: TestOptionsMessage, test: TestFunction): T;
  }) {
    return schema.test(
      'requiredIfVisible',
      i18next.t('validations.mixed.required'),
      requiredIfVisible,
    );
  }

  const fieldType = field.fieldInfo.type;
  if (fieldType === FieldType.Boolean) {
    return addRequiredIfVisible(yup.boolean());
  } else if (
    fieldType === FieldType.Integer ||
    fieldType === FieldType.Currency ||
    fieldType === FieldType.Double
  ) {
    return addRequiredIfVisible(
      yup
        .mixed()
        .transform((value) => {
          if (value === '' || value === undefined) {
            return null;
          }
          const num = Number(value);
          return isNaN(num) ? null : num;
        })
        .nullable()
        .test('is-number', i18next.t('validations.mixed.default'), (value) => {
          return value === null || typeof value === 'number';
        }),
    );
  } else if (fieldType === FieldType.Date) {
    return addRequiredIfVisible(
      yup
        .mixed()
        .transform((value) => {
          if (value === '' || value === undefined) {
            return null;
          }
          const date = value instanceof Date ? value : new Date(value);
          return isNaN(date.getTime()) ? null : date;
        })
        .nullable()
        .test('is-date', i18next.t('validations.mixed.default'), (value) => {
          return value === null || value instanceof Date;
        }),
    );
  } else if (fieldType === FieldType.Datetime) {
    return addRequiredIfVisible(
      yup
        .mixed()
        .transform((value) => {
          if (value === '' || value === undefined) {
            return null;
          }
          const date = value instanceof Date ? value : new Date(value);
          return isNaN(date.getTime()) ? null : date;
        })
        .nullable()
        .test('is-date', i18next.t('validations.mixed.default'), (value) => {
          return value === null || value instanceof Date;
        }),
    );
  }
  return addRequiredIfVisible(yup.string());
}
